import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap,
  useNodesState, useEdgesState, ReactFlowProvider, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import dagre from "dagre";
import axios from "axios";
import EmployeeNode from "@/components/EmployeeNode";
import MultiSelect from "@/components/MultiSelect";
import { API_BASE } from "@/lib/api";
import { Filter, X, ShieldCheck } from "lucide-react";

const NODE_W = 240;
const NODE_H = 96;
const nodeTypes = { employee: EmployeeNode };

function layoutGraph(nodes, edges) {
  const g = new dagre.graphlib.Graph();
  g.setDefaultEdgeLabel(() => ({}));
  g.setGraph({ rankdir: "TB", nodesep: 40, ranksep: 80 });
  nodes.forEach((n) => g.setNode(n.id, { width: NODE_W, height: NODE_H }));
  edges.forEach((e) => g.setEdge(e.source, e.target));
  dagre.layout(g);
  return nodes.map((n) => {
    const p = g.node(n.id);
    return { ...n, position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 } };
  });
}

function matches(e, f) {
  if (f.departments.length > 0 && !f.departments.includes(e.department || "")) return false;
  if (f.designations.length > 0 && !f.designations.includes(e.designation || "")) return false;
  return true;
}

function Inner() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [filters, setFilters] = useState({ departments: [], designations: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/share/${token}`);
        // Rewrite photo_path to use public share file endpoint
        data.employees = data.employees.map((e) => ({
          ...e,
          photo_url: e.photo_url || (e.photo_path ? `${API_BASE}/share-files/${token}/${e.photo_path}` : ""),
          photo_path: "",
        }));
        setData(data);
      } catch (e) {
        setError(e.response?.data?.detail || "Link is invalid or expired");
      }
    })();
  }, [token]);

  const departments = useMemo(
    () => Array.from(new Set((data?.employees || []).map((e) => e.department).filter(Boolean))).sort(),
    [data],
  );
  const designations = useMemo(
    () => Array.from(new Set((data?.employees || []).map((e) => e.designation).filter(Boolean))).sort(),
    [data],
  );
  const filtering = filters.departments.length > 0 || filters.designations.length > 0;

  const handleSelect = useCallback(() => {}, []);

  useEffect(() => {
    if (!data) return;
    const matchedIds = new Set(data.employees.filter((e) => matches(e, filters)).map((e) => e.id));
    const n = data.employees.map((e) => ({
      id: e.id,
      type: "employee",
      position: { x: 0, y: 0 },
      data: { employee: e, onSelect: handleSelect },
      style: filtering && !matchedIds.has(e.id) ? { opacity: 0.18 } : { opacity: 1 },
    }));
    const ed = data.employees
      .filter((e) => e.manager_id)
      .map((e) => {
        const dim = filtering && !(matchedIds.has(e.id) && matchedIds.has(e.manager_id));
        return {
          id: `e-${e.manager_id}-${e.id}`,
          source: e.manager_id,
          target: e.id,
          type: "smoothstep",
          style: { stroke: dim ? "#D4D4D2" : "#111", strokeWidth: 1, opacity: dim ? 0.45 : 1 },
          markerEnd: { type: MarkerType.ArrowClosed, color: dim ? "#D4D4D2" : "#111", width: 14, height: 14 },
        };
      });
    setNodes(layoutGraph(n, ed));
    setEdges(ed);
  }, [data, filters, filtering, handleSelect, setNodes, setEdges]);

  if (error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F4F2] px-6">
        <div className="max-w-sm border border-neutral-300 bg-white p-8 text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            Access Denied
          </p>
          <h2 className="mt-2 text-2xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
            {error}
          </h2>
          <p className="mt-3 text-sm text-neutral-600">
            Please request a new link from the organization's admin.
          </p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F4F4F2]">
        <div className="text-xs uppercase tracking-[0.2em] text-neutral-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col bg-[#F4F4F2]" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
      <header className="border-b border-neutral-200 bg-white px-6 py-4">
        <div className="flex items-end justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
              Public Org Chart · Read-only
            </p>
            <h1 className="mt-1 text-2xl tracking-tight" style={{ fontFamily: '"Work Sans", sans-serif' }}>
              {data.company_name}
            </h1>
            <p className="text-sm text-neutral-600">{data.name} — {data.employees.length} people</p>
          </div>
          <p className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
            <ShieldCheck size={12}/>
            Shared link
          </p>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-3 border-b border-neutral-200 bg-white px-6 py-3">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-neutral-500" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>
          <Filter size={13}/>
          Filter
        </div>
        <MultiSelect options={departments} value={filters.departments} onChange={(v) => setFilters((f) => ({ ...f, departments: v }))} label="All departments" testId="public-filter-department"/>
        <MultiSelect options={designations} value={filters.designations} onChange={(v) => setFilters((f) => ({ ...f, designations: v }))} label="All designations" testId="public-filter-designation"/>
        {filtering && (
          <button
            onClick={() => setFilters({ departments: [], designations: [] })}
            className="flex h-8 items-center gap-1 border border-neutral-300 px-2 text-[11px] uppercase tracking-wider text-neutral-700 hover:border-black"
            style={{ fontFamily: '"IBM Plex Mono", monospace' }}
          >
            <X size={11}/>
            Clear
          </button>
        )}
      </div>

      <div className="relative flex-1">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.2 }}
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
        >
          <Background gap={20} color="#D4D4D2"/>
          <Controls className="!rounded-none !border-neutral-300 !shadow-none [&_button]:!rounded-none" showInteractive={false}/>
          <MiniMap pannable className="!border !border-neutral-300" maskColor="rgba(244,244,242,0.6)" nodeColor="#002FA7"/>
        </ReactFlow>
      </div>
    </div>
  );
}

export default function SharedOrgChart() {
  return (
    <ReactFlowProvider>
      <Inner/>
    </ReactFlowProvider>
  );
}
